import * as React from 'react';

import moment from 'moment';

import type {Assignable} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {TaskAssigneePopover} from 'shells/row/task-row/task-assignee-popover';
import {TaskAssigneeSelector} from 'shells/row/task-row/task-assignee-selector';
import {TaskStatusLabel} from 'shells/row/task-row/task-status-label';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {
    CoreEntityTaskFragment_assigner as Assigner,
    TaskCreateInput,
    TaskUpdateInput,
    GetDefaultTask_taskDefaults as TaskDefaults,
} from 'nutshell-graphql-types';

import {getDefaultTaskDueTime} from '../helpers';

import styles from '../tasks-widget.module.css';

type Props = {
    defaultTask: TaskDefaults;
    entityId?: string;
    assignables: Assignable[];
    onCancel: () => void;
    onCreate?: (arg1: TaskCreateInput) => void;
    onUpdate?: (arg1: TaskUpdateInput) => void;
    isSaving?: boolean;
    assigner?: Assigner | null | undefined;
    assignedTime?: number | null | undefined;
};

export function TaskForm(props: Props) {
    const {onCancel, onCreate, onUpdate, entityId, defaultTask} = props;

    const [name, setName] = React.useState<string>(props.defaultTask.title || '');
    const [dueTime, setDueTime] = React.useState<number>(() =>
        getDefaultTaskDueTime(props.defaultTask.dueTime)
    );
    const [assigneeId, setAssigneeId] = React.useState<string | null | undefined>(
        props.defaultTask.assignee && props.defaultTask.assignee.id
    );
    const [note, setNote] = React.useState<string>(props.defaultTask.description || '');
    const [isAssigneePopoverOpen, setIsAssigneePopoverOpen] = React.useState<boolean>(false);

    const assigneeRef = React.useRef<HTMLElement | null | undefined>(null);

    React.useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (onUpdate) {
                    onCancel();
                } else if (onCreate) {
                    const noChanges =
                        name === (props.defaultTask.title || '') &&
                        note === (props.defaultTask.description || '') &&
                        assigneeId ===
                            (props.defaultTask.assignee && props.defaultTask.assignee.id) &&
                        dueTime === getDefaultTaskDueTime(props.defaultTask.dueTime);

                    if (noChanges) {
                        onCancel();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [
        onUpdate,
        onCreate,
        onCancel,
        name,
        props.defaultTask.title,
        props.defaultTask.description,
        props.defaultTask.assignee,
        props.defaultTask.dueTime,
        note,
        assigneeId,
        dueTime,
    ]);

    const handleSaveTask = React.useCallback(() => {
        if (onCreate && entityId) {
            onCreate({
                name,
                dueTime,
                assignee: assigneeId,
                description: note,
                entity: entityId,
            });
        } else if (onUpdate) {
            onUpdate({
                task: defaultTask.id,
                name,
                dueTime,
                assignee: assigneeId,
                description: note,
            });
        }
    }, [name, dueTime, assigneeId, note, entityId, onCreate, onUpdate, defaultTask]);

    const handleCancel = React.useCallback(() => {
        onCancel();
    }, [onCancel]);

    const isDisabled = name.trim() === '';

    const handleEnter = React.useCallback(() => {
        if (!isDisabled) {
            handleSaveTask();
        }
    }, [handleSaveTask, isDisabled]);

    const hasAssigneeChanged =
        props.defaultTask.assignee && props.defaultTask.assignee.id !== assigneeId;

    const hasLeadM3 = NutClientConfig.hasLeadPageRedesignM3FF;

    return (
        <div className='flex flex-dir-col gap-4 mb-8'>
            <div className='flex gap-4 align-center'>
                <TextField
                    value={name}
                    onChange={setName}
                    placeholder='Task name'
                    size='small'
                    focusOnMount={true}
                    onEnter={handleEnter}
                />
                {!hasLeadM3 && (
                    <div className={styles['assignee-select']}>
                        <AssigneeSelect
                            options={props.assignables}
                            value={assigneeId}
                            isClearable={true}
                            onChange={setAssigneeId}
                        />
                    </div>
                )}
            </div>
            <div className='flex gap-8 align-center'>
                <TaskStatusLabel
                    dueTime={dueTime}
                    isOverdue={false}
                    completedBy={null}
                    completedTime={null}
                    onDueTimeChange={setDueTime}
                    isAutomated={false}
                    isSkipped={false}
                />
                {hasLeadM3 && (
                    <TaskAssigneeSelector
                        assignables={props.assignables}
                        assigneeId={assigneeId}
                        getAssigneeRef={(ref) => {
                            assigneeRef.current = ref;
                        }}
                        onAssigneePopoverOpen={setIsAssigneePopoverOpen}
                    />
                )}
            </div>
            {props.assigner && props.assignedTime && (
                <Body
                    size='small'
                    color='light'
                    title={
                        hasAssigneeChanged
                            ? undefined
                            : `Assigned on ${moment
                                  .unix(props.assignedTime)
                                  .format('MMM Do, YYYY, h:mm A')}`
                    }
                >
                    Assigned by {hasAssigneeChanged ? 'you' : props.assigner && props.assigner.name}
                </Body>
            )}
            <TextField
                placeholder='Add a note…'
                size='small'
                value={note}
                onChange={setNote}
                multiline={true}
                rows={2}
            />
            <div className='flex justify-end gap-4'>
                <Button variant='text-primary' onClick={handleCancel}>
                    Cancel
                </Button>
                <SaveButton
                    // @ts-expect-error
                    onClick={handleSaveTask}
                    disabled={isDisabled}
                    disabledTooltip='Task name is required'
                    isSaving={props.isSaving || false}
                    buttonText={onCreate ? 'Add task' : 'Save task'}
                    savingText={onCreate ? 'Adding task…' : 'Saving task…'}
                    successText={onCreate ? 'Task added!' : 'Task saved!'}
                />
            </div>
            {isAssigneePopoverOpen && (
                <TaskAssigneePopover
                    anchor={assigneeRef.current}
                    assignables={props.assignables}
                    assigneeId={assigneeId}
                    setIsAssigneePopoverOpen={setIsAssigneePopoverOpen}
                    onSelect={(id) => {
                        setAssigneeId(id);
                        setIsAssigneePopoverOpen(false);
                    }}
                />
            )}
        </div>
    );
}
